import { useState, useEffect, useRef } from 'react';
import { trackWhatsAppClick } from '@/lib/gtag';
import { MessageCircle, X, Bot, HelpCircle } from 'lucide-react';

export default function WhatsAppFloat() {
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Handle clicking outside to close the popover
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Inject CSS style to hide the default Readdy AI widget button permanently inside its shadow DOM
  useEffect(() => {
    const hideDefaultWidgetButton = () => {
      const vapiWidget = document.querySelector('vapi-widget');
      if (vapiWidget && vapiWidget.shadowRoot) {
        // Check if our custom style tag is already injected
        if (vapiWidget.shadowRoot.getElementById('antigravity-hide-vapi-btn')) {
          return true;
        }

        const style = document.createElement('style');
        style.id = 'antigravity-hide-vapi-btn';
        style.textContent = `
          button.vapi-btn, 
          [role="button"].vapi-btn, 
          .vapi-btn, 
          button:has(svg),
          button {
            opacity: 0 !important;
            pointer-events: none !important;
            width: 0px !important;
            height: 0px !important;
            transform: scale(0) !important;
            position: absolute !important;
            left: -9999px !important;
          }
        `;
        vapiWidget.shadowRoot.appendChild(style);
        return true;
      }
      return false;
    };

    // Poll periodically to catch the element once loaded
    const success = hideDefaultWidgetButton();
    if (!success) {
      const interval = setInterval(() => {
        if (hideDefaultWidgetButton()) {
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, []);

  const handleAIClick = () => {
    setIsOpen(false);
    
    // Programmatically trigger Readdy AI assistant
    const vapiWidget = document.querySelector('vapi-widget');
    if (vapiWidget && vapiWidget.shadowRoot) {
      // Find the button (even if hidden by our styles, click still triggers)
      const btn = vapiWidget.shadowRoot.querySelector('button') || 
                  vapiWidget.shadowRoot.querySelector('[role="button"]') ||
                  vapiWidget.shadowRoot.querySelector('.vapi-btn');
      if (btn) {
        (btn as HTMLElement).click();
      }
    }
  };

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      className="fixed bottom-6 right-6 z-[60] flex flex-col items-end"
    >
      {/* Popover Menu */}
      <div
        className={`w-80 bg-white border border-stone-200 shadow-2xl transition-all duration-300 mb-3 origin-bottom-right ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-stone-950 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-wider">Support Channels</span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-stone-400 hover:text-white transition-colors"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Options */}
        <div className="flex flex-col">
          {/* Option 1: WhatsApp */}
          <a
            href="https://wa.me/6585353868"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setIsOpen(false);
              trackWhatsAppClick('floating_widget_whatsapp');
            }}
            className="flex items-start gap-3.5 p-4 hover:bg-stone-50 transition-colors text-left group"
          >
            <div className="w-10 h-10 flex-shrink-0 bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center transition-colors group-hover:bg-emerald-600 group-hover:text-white">
              <i className="ri-whatsapp-line text-xl" />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900 group-hover:text-emerald-700 transition-colors">
                Chat with Specialist
              </p>
              <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                Connect with our local team on WhatsApp for site measurements and custom door/window quotes.
              </p>
            </div>
          </a>

          {/* Option 2: AI Assistant */}
          <button
            onClick={handleAIClick}
            className="flex items-start gap-3.5 p-4 hover:bg-stone-50 transition-colors text-left border-t border-stone-150 group w-full"
          >
            <div className="w-10 h-10 flex-shrink-0 bg-stone-100 border border-stone-200 text-stone-700 flex items-center justify-center transition-colors group-hover:bg-stone-900 group-hover:text-white">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-900 group-hover:text-stone-700 transition-colors flex items-center gap-1.5">
                Talk to AI Assistant
                <span className="inline-flex items-center rounded-none bg-stone-100 px-1.5 py-0.5 text-[9px] font-medium text-stone-800">
                  24/7
                </span>
              </p>
              <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                Ask about glass specifications, architectural models, pricing guidelines, or request call-backs instantly.
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Main Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label="Toggle contact support options"
        className={`flex items-center gap-2.5 px-4 py-3 bg-neutral-900 text-white hover:bg-neutral-800 transition-all duration-300 shadow-xl border border-neutral-800 rounded-full ${
          isOpen ? 'bg-neutral-800' : ''
        }`}
      >
        {isOpen ? (
          <X className="h-4 w-4" />
        ) : (
          <MessageCircle className="h-4 w-4" />
        )}
        <span className="text-xs font-bold uppercase tracking-widest">
          {isOpen ? 'Close support' : 'Support Specialists & AI'}
        </span>
      </button>
    </div>
  );
}